import { MrfLineItem } from "./mrf-line-item.model"
import { v4 as uuid } from 'uuid'
import { Location } from "./location.model"
import { MrfPhoto } from "./mrf-photo.model"

// rt2.mrf_statuses ids. InProgress is the app's Draft.
export enum MrfStatusId {
  Submitted = 0,
  Rejected = 1,
  POSubmittedParts = 2,
  POSubmittedServices = 3,
  Completed = 4,
  Removed = 5,
  InProgress = 6,
  Approved = 7,
}

export const MRF_STATUS_NAMES: { [id: number]: string } = {
  0: 'Submitted',
  1: 'Rejected',
  2: 'PO Submitted Parts',
  3: 'PO Submitted Services',
  4: 'Completed',
  5: 'Removed',
  6: 'InProgress',
  7: 'Approved',
}

export class Mrf {
  uuid: string
  id: number
  is_pending_save: boolean
  is_pending_delete: boolean
  mrf_status_id: number
  engine_make_model: string
  compressor_make_model: string
  mrf_notes: string
  work_order_id: number
  work_order_number: string
  mrf_line_items: MrfLineItem[]
  status: string
  created_at: string
  updated_at?: string
  company_name: string
  requestor: string
  requestor_id?: number
  last_modified_by: string
  location_name: string
  unit_number: string
  job_type: string
  rejected_reason?: string | null
  mrf_type_id?: number
  vendor_po_number?: string | null

  // App only. pending_photo_deletes holds the uuids of uploaded photos removed
  // here and not yet deleted on the server. local_rev changes on every local
  // save, so a sync can tell whether the MRF was edited while it ran.
  pending_photo_deletes: string[]
  local_rev?: number

  constructor() {
  }

  public async setViewModelFields()
  {
    if (!this.id) {
      this.id = 0
    }
    if (!this.uuid) {
      this.uuid = uuid();
    }
    if (!this.status) {
      this.status = ''
    }
    if (this.is_pending_save === undefined) {
      this.is_pending_save = false
    }
    if (this.is_pending_delete === undefined) {
      this.is_pending_delete = false
    }
    if (!Array.isArray(this.pending_photo_deletes)) {
      this.pending_photo_deletes = []
    }
    this.mrf_line_items = (Array.isArray(this.mrf_line_items) ? this.mrf_line_items : []).map((l: any) => ({
      ...l,
      photos: MrfPhoto.buildMany(l?.photos),
    }))
  }

  static buildOne(obj: any): Mrf {
    let target: Mrf = Object.assign(new Mrf(), obj)
    target.setViewModelFields()
    return target
  }

  static buildMany(objs: any[]): Mrf[] {
    const lobjs = []
    for (const i in objs) {
      if (objs.hasOwnProperty(i)) {
        lobjs.push(Mrf.buildOne(objs[i]))
      }
    }
    return lobjs
  }

  // A technician can change an MRF from the app while it's a draft or parts
  // has sent it back; submitted and later are read only.
  static isEditable(mrf: Mrf | null | undefined): boolean {
    if (!mrf) return true
    if (mrf.is_pending_delete) return false
    return mrf.mrf_status_id === MrfStatusId.InProgress || mrf.mrf_status_id === MrfStatusId.Rejected
  }

  // The server's own rule for a technician delete.
  static isDeletable(mrf: Mrf | null | undefined): boolean {
    if (!mrf || mrf.is_pending_delete) return false
    return [MrfStatusId.Submitted, MrfStatusId.Rejected, MrfStatusId.InProgress].includes(mrf.mrf_status_id)
  }

  static statusName(mrf: Mrf): string {
    return MRF_STATUS_NAMES[mrf.mrf_status_id] ?? mrf.status ?? ''
  }

  // The body for POST mrf/ and PUT mrf/:id: no photos (they go one at a time
  // to mrf/photos) and nothing that only the app keeps.
  static toApiPayload(mrf: Mrf): any {
    const lines = (mrf.mrf_line_items || []).map((l: MrfLineItem) => {
      const line: any = {
        id: l.id > 0 ? l.id : 0,
        uuid: l.uuid,
        source: l.source ?? '',
        part_number: l.part_number ?? '',
        description: l.description ?? '',
        quantity: Number.parseInt(String(l.quantity ?? ''), 10) || 0,
      }
      // The API keeps its own value only when these are left out entirely.
      if (l.manufacturer != null) line.manufacturer = l.manufacturer
      if (l.price != null) line.price = l.price
      if (l.unit_of_measure_id != null) line.unit_of_measure_id = l.unit_of_measure_id
      return line
    })
    const payload: any = {
      id: mrf.id > 0 ? mrf.id : 0,
      uuid: mrf.uuid,
      work_order_id: mrf.work_order_id,
      engine_make_model: mrf.engine_make_model ?? '',
      compressor_make_model: mrf.compressor_make_model ?? '',
      mrf_notes: mrf.mrf_notes ?? '',
      mrf_status_id: mrf.mrf_status_id,
      status: mrf.status,
      created_at: mrf.created_at,
      mrf_line_items: lines,
    }
    if (mrf.mrf_type_id != null) payload.mrf_type_id = mrf.mrf_type_id
    if (mrf.vendor_po_number != null) payload.vendor_po_number = mrf.vendor_po_number
    return payload
  }
}
